/**
 * Bus view — canonical-centric topology.
 *
 * Layout:
 *   ┌────────────┐  ┌─────────────────────────────────────────────┐
 *   │ Canonical  │  │ PRODUCERS  │  Canonical  │  CONSUMERS       │
 *   │   list     │  │  (left)    │   (centre)  │   (right)        │
 *   │ (sidebar)  │  │            │             │                  │
 *   └────────────┘  └─────────────────────────────────────────────┘
 *
 * Answers the question "what touches X canonical?" much more directly
 * than the map view does — one row per producer/consumer workflow,
 * with the ingress source (or sink target) hung off each row.
 *
 * Each row links to its underlying workflow editor.
 */
import { ArrowRight, Boxes, ChevronRight, Inbox, Send } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { TagList } from "@/components/tags";
import { Badge } from "@/components/ui/badge";
import {
  type TopologyEdge,
  type TopologyNode,
  type TopologyResponse,
} from "@/lib/api-topology";
import { cn } from "@/lib/cn";


export interface BusViewProps {
  data: TopologyResponse;
}

export function BusView({ data }: BusViewProps) {
  const { t } = useTranslation();
  const canonicals = React.useMemo(
    () =>
      data.nodes
        .filter((n) => n.type === "canonical")
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data.nodes],
  );

  const [selectedId, setSelectedId] = React.useState<string | null>(
    canonicals[0]?.id ?? null,
  );

  // Auto-select first canonical on initial mount + when the list
  // arrives. Don't overwrite if the user already picked one that
  // still exists.
  React.useEffect(() => {
    if (!selectedId || !canonicals.some((c) => c.id === selectedId)) {
      setSelectedId(canonicals[0]?.id ?? null);
    }
  }, [canonicals, selectedId]);

  const selected = canonicals.find((c) => c.id === selectedId) ?? null;

  if (canonicals.length === 0) {
    return (
      <div className="grid h-full place-items-center text-sm text-muted-foreground">
        {t("topology.bus.noCanonicals", "No canonical models active yet.")}
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left sidebar: canonical list */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-border bg-surface">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
          {t("topology.bus.canonicals", "Canonical models")}
        </div>
        <ul className="flex-1 overflow-y-auto">
          {canonicals.map((c) => {
            const isActive = c.id === selectedId;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(c.id)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition border-l-2 border-transparent",
                    "hover:bg-muted",
                    isActive && "bg-muted border-accent text-foreground",
                  )}
                >
                  <Boxes
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-[12px]">{c.label}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {c.sublabel}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Main: producers — canonical — consumers */}
      <div className="flex-1 overflow-auto">
        {selected ? <BusFocus canonical={selected} all={data} /> : null}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Producers + Consumers for the selected canonical
// ---------------------------------------------------------------------------

interface BusFocusProps {
  canonical: TopologyNode;
  all: TopologyResponse;
}

function BusFocus({ canonical, all }: BusFocusProps) {
  const { t } = useTranslation();

  // Producers: edges where target == this canonical. The "left side"
  // shows: ingress-source → workflow → canonical.
  const producers = React.useMemo(
    () =>
      all.edges
        .filter((e) => e.target === canonical.id)
        .sort((a, b) => a.workflow_name.localeCompare(b.workflow_name)),
    [all.edges, canonical.id],
  );

  // Consumers: edges where source == this canonical. The "right side"
  // shows: canonical → workflow → sink.
  const consumers = React.useMemo(
    () =>
      all.edges
        .filter((e) => e.source === canonical.id)
        .sort((a, b) => a.workflow_name.localeCompare(b.workflow_name)),
    [all.edges, canonical.id],
  );

  const nodeById = React.useMemo(() => {
    const map = new Map<string, TopologyNode>();
    all.nodes.forEach((n) => map.set(n.id, n));
    return map;
  }, [all.nodes]);

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      {/* Canonical card on top */}
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/10 text-accent">
            <Boxes className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-mono text-base font-medium">{canonical.label}</div>
            <div className="text-xs text-muted-foreground">{canonical.sublabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{producers.length} producers</span>
          <span>·</span>
          <span>{consumers.length} consumers</span>
        </div>
      </header>

      {canonical.tags.length > 0 && (
        <div className="mb-4">
          <TagList tags={canonical.tags} />
        </div>
      )}

      {/* Two columns: producers (left, with reversed flow arrow →
          canonical) and consumers (right, flow from canonical → sink). */}
      <div className="grid flex-1 grid-cols-1 gap-6 overflow-auto md:grid-cols-2">
        <section>
          <SectionHeader
            icon={Inbox}
            label={t("topology.bus.producers", "Producers")}
            count={producers.length}
            tone="success"
          />
          {producers.length === 0 ? (
            <EmptyHint
              text={t(
                "topology.bus.noProducers",
                "Nothing writes to this canonical yet.",
              )}
            />
          ) : (
            <ul className="space-y-2">
              {producers.map((e) => {
                const other = nodeById.get(e.source);
                return (
                  <FlowRow
                    key={e.id}
                    edge={e}
                    other={other ?? null}
                    direction="producer"
                  />
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <SectionHeader
            icon={Send}
            label={t("topology.bus.consumers", "Consumers")}
            count={consumers.length}
            tone="warning"
          />
          {consumers.length === 0 ? (
            <EmptyHint
              text={t(
                "topology.bus.noConsumers",
                "Nothing reads from this canonical yet.",
              )}
            />
          ) : (
            <ul className="space-y-2">
              {consumers.map((e) => {
                const other = nodeById.get(e.target);
                return (
                  <FlowRow
                    key={e.id}
                    edge={e}
                    other={other ?? null}
                    direction="consumer"
                  />
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

interface SectionHeaderProps {
  icon: typeof Inbox;
  label: string;
  count: number;
  tone: "success" | "warning";
}

function SectionHeader({ icon: Icon, label, count, tone }: SectionHeaderProps) {
  return (
    <header
      className={cn(
        "mb-2 flex items-center gap-2 border-b border-border pb-1.5 text-xs uppercase tracking-wider",
        tone === "success" ? "text-success" : "text-warning",
      )}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      <span className="font-medium">{label}</span>
      <span className="ml-auto font-mono text-[11px] text-muted-foreground">
        {count}
      </span>
    </header>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-surface px-3 py-4 text-center text-xs text-muted-foreground">
      {text}
    </div>
  );
}

interface FlowRowProps {
  edge: TopologyEdge;
  other: TopologyNode | null;
  direction: "producer" | "consumer";
}

function FlowRow({ edge, other, direction }: FlowRowProps) {
  const otherIcon =
    other?.type === "ingress"
      ? Inbox
      : other?.type === "external_sink"
        ? Send
        : Boxes;
  const Icon = otherIcon;

  // Producer row: [endpoint] → [workflow]
  // Consumer row: [workflow] → [endpoint]
  const endpointCol = (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">
          {other?.label ?? "—"}
        </div>
        {other?.sublabel && (
          <div className="truncate text-[11px] text-muted-foreground">
            {other.sublabel}
          </div>
        )}
      </div>
    </div>
  );

  const m = edge.metrics;
  const hasFailure = m.runs_failed > 0;
  const isIdle = m.runs_total === 0;
  const workflowCol = (
    <Link
      to={`/workflows/${edge.workflow_id}`}
      className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-elevated px-2 py-1.5 transition hover:border-accent"
    >
      <span className="truncate font-mono text-[12px]">{edge.workflow_name}</span>
      {/* Run-health dot: red on failures, amber on idle, green when
          the last run succeeded with traffic. Tooltip shows the
          breakdown over the trailing window. */}
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          hasFailure
            ? "bg-danger"
            : isIdle
              ? "bg-muted-foreground/40"
              : "bg-success",
        )}
        title={
          isIdle
            ? "No runs in the last 24h"
            : `${m.runs_succeeded} ok / ${m.runs_failed} failed / ${m.runs_running} running (24h)`
        }
      />
      <Badge tone={edge.enabled ? "active" : "neutral"} className="ml-auto shrink-0">
        {edge.direction}
      </Badge>
      <ChevronRight
        className="h-3 w-3 shrink-0 text-muted-foreground"
        strokeWidth={1.5}
      />
    </Link>
  );

  return (
    <li className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
      {direction === "producer" ? endpointCol : workflowCol}
      <ArrowRight
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
        strokeWidth={1.5}
      />
      {direction === "producer" ? workflowCol : endpointCol}
    </li>
  );
}
